import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Receipt, Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, fmtDate } from "@/pages/command/ui";

const METHODS = ["Cash", "Check", "Card", "Transfer", "Cashapp", "Zelle", "Invoice", "Apple Pay", "Other"];
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const empty = (clientId) => ({ client_id: clientId, description: "", amount: "", amount_paid: "", card_balance: "", due_date: "", paid_date: "", method: "none", notes: "" });

export function ClientBilling({ clientId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty(clientId));
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/cc/payments", { params: { client_id: clientId } }); setRows(data); } catch { /* ignore */ }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditId(null); setForm(empty(clientId)); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ ...empty(clientId), ...p, method: p.method || "none", amount: p.amount ?? "", amount_paid: p.amount_paid ?? "", card_balance: p.card_balance ?? "" }); setOpen(true); };
  const save = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    setSaving(true);
    try {
      const payload = { ...form, client_id: clientId, method: form.method === "none" ? null : form.method, amount: parseFloat(form.amount || 0), amount_paid: parseFloat(form.amount_paid || 0), card_balance: form.card_balance === "" ? null : parseFloat(form.card_balance) };
      delete payload.id; delete payload.balance; delete payload.client_name; delete payload.status; delete payload.created_at; delete payload.updated_at;
      if (editId) await api.put(`/cc/payments/${editId}`, payload);
      else await api.post("/cc/payments", payload);
      toast.success("Saved"); setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  const remove = async (p) => { if (!window.confirm("Delete this payment?")) return; try { await api.delete(`/cc/payments/${p.id}`); setRows((r) => r.filter((x) => x.id !== p.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };

  const outstanding = rows.filter((p) => p.status !== "Paid").reduce((s, p) => s + Number(p.balance ?? p.amount ?? 0), 0);

  return (
    <div data-testid="client-billing">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Receipt size={20} className="text-primary" /><h2 className="text-lg font-medium">Billing &amp; Payments</h2>{outstanding > 0 && <span className="text-xs text-amber-300">· {money(outstanding)} due</span>}</div>
        <Button size="sm" onClick={openNew} data-testid="client-new-payment-button" className="bg-primary text-primary-foreground hover:bg-[#DBC197] gap-1"><Plus size={16} weight="bold" /> Log payment</Button>
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-muted-foreground">No payments logged for this client yet.</div>
      ) : (
        <div className="rounded-xl border border-white/10 bg-zinc-900/50 divide-y divide-white/5" data-testid="client-payments-list">
          {rows.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-4 px-4 py-3.5" data-testid={`client-payment-${p.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{p.description}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {p.method || "—"} · due {fmtDate(p.due_date)}{Number(p.amount_paid) > 0 && Number(p.balance) > 0 ? ` · paid ${money(p.amount_paid)} · balance ${money(p.balance)}` : ""}{p.card_balance != null ? ` · card ${money(p.card_balance)}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-display text-lg tracking-tighter">{money(p.amount)}</span>
                <StatusBadge status={p.status === "Paid" ? "Completed" : p.status === "Partial" ? "In Progress" : "Waiting"} />
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-client-payment-${p.id}`}><PencilSimple size={16} /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-client-payment-${p.id}`} className="text-red-300 hover:text-red-200"><Trash size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 max-w-lg max-h-[85vh] overflow-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Payment" : "Log Payment"}</DialogTitle><DialogDescription className="text-sm text-muted-foreground">Status auto-sets from amounts (Unpaid / Partial / Paid).</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Description *</Label><Input data-testid="client-payment-description" value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount due ($)</Label><Input data-testid="client-payment-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} /></div>
              <div><Label>Amount paid ($)</Label><Input data-testid="client-payment-paid" type="number" min="0" step="0.01" value={form.amount_paid} onChange={(e) => set("amount_paid", e.target.value)} /></div>
              <div><Label>Credit card balance ($)</Label><Input data-testid="client-payment-card-balance" type="number" min="0" step="0.01" value={form.card_balance} onChange={(e) => set("card_balance", e.target.value)} placeholder="Optional" /></div>
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => set("method", v)}>
                  <SelectTrigger data-testid="client-payment-method"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">—</SelectItem>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Due date</Label><Input type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-client-payment" disabled={saving} onClick={save} className="bg-primary text-primary-foreground hover:bg-[#DBC197]">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
